import { useState, useEffect, useLayoutEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setSelectedVersion } from '../project-details/data-access/project.reducer';
import { Row, Col, Container, Card, Accordion } from 'react-bootstrap';
import { selectProjectResults } from '../project-details/data-access/project.reducer';
import VersionHistory, { VersionItem } from '../../../components/common/VersionHistory';
import { Typography } from '@mui/material';
import BiasMLChart from '../Dashboard/Bias/BiasChartMl';
import { BiasModuleKpiGrid } from './ModuleKpiDashboard';
import BiasDeveloperRemediationSection from './BiasDeveloperRemediationSection';
import './BiasDetectionChartsSection.css';
import type { BiasMLDataItem } from '../Dashboard/Bias/BiasChartMl';
import { pickBestAggregateSummary } from '../../../utils/aggregateSummaryPick';
import { pickLatestCollectionRun } from '../../../utils/collectionRuns';
import { isWorkbenchNonRerunRun } from '../../../utils/workbenchRunFilter';
import { coalesceAggregateScalarsFromCandidates, mergeSparseAggregateSummary } from './biasSummaryReference.utils';

/**
 * Display string for version timestamps from the API (ISO strings, epoch ms, Mongo `{ $date }`, etc.).
 * Avoids coercing objects with `String(obj)` → "[object Object]".
 */
/** Match `--phoenix-navbar-top-height` for sticky offset and scroll “stuck” detection. */
function readPhoenixNavbarTopPx(): number {
  if (typeof window === 'undefined') return 72;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--phoenix-navbar-top-height').trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 72;
}

function formatBiasVersionTimestamp(ts: unknown): string {
  if (ts == null || ts === '') return '';
  if (typeof ts === 'number' && !Number.isNaN(ts)) {
    try {
      return new Date(ts).toISOString();
    } catch {
      return String(ts);
    }
  }
  if (typeof ts === 'string') {
    const s = ts.trim();
    if (!s) return '';
    if (s.includes('$date')) {
      try {
        const parsed = JSON.parse(s) as { $date?: unknown };
        const ms = parsed?.$date;
        if (typeof ms === 'number') return new Date(ms).toISOString();
        if (typeof ms === 'string' && ms.trim()) return ms.trim();
      } catch {
        return s;
      }
    }
    return s;
  }
  if (typeof ts === 'object' && !Array.isArray(ts) && ts !== null) {
    const o = ts as Record<string, unknown>;
    if (o.$date != null) {
      const ms = o.$date;
      if (typeof ms === 'number') return new Date(ms).toISOString();
      if (typeof ms === 'string' && ms.trim()) {
        const n = Number(ms);
        if (!Number.isNaN(n) && ms.length > 4) return new Date(n).toISOString();
        return ms.trim();
      }
    }
    for (const k of ['iso', 'ISO', 'value', 'display', 'formatted', 'timestamp', 'date'] as const) {
      const v = o[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
      if (typeof v === 'number' && !Number.isNaN(v)) {
        try {
          return new Date(v).toISOString();
        } catch {
          /* ignore */
        }
      }
    }
  }
  return '';
}

type BiasDataItem = {
  Attribute?: string;
  Risk?: string;
  [key: string]: string | number | undefined;
};

type LLMBiasDetectionItem = {
  result_id: string;
  title: string;
  timestamp: string;
  analysis_date: string;
  use_whitelist: string;
  domain: string;
  userId: string;
  aggregate_summary: any;
  data: BiasDataItem[];
};

type MLBiasDetectionItem = {
  result_id: string;
  title: string;
  timestamp: string;
  analysis_date: string;
  use_whitelist: string;
  domain: string;
  userId: string;
  aggregate_summary: any;
  data: BiasDataItem[];
};

const HOISTABLE_AGG_KEYS = [
  'developer_recommendations',
  'avg_metrics',
  'audit_avg_metrics',
  'audited_avg_metrics',
  'sensitive_density',
  'sensitive_by_source',
  'non_whitelisted_sensitive_by_source',
  'attribute_mentions',
  'verdict_distribution',
  'funnel',
  'metric_thresholds',
  'thresholds',
  'vulnerability_surface'
] as const;

const isPopulatedObject = (v: unknown): boolean =>
  v != null && (typeof v !== 'object' || Array.isArray(v) || Object.keys(v as object).length > 0);

/**
 * Builds an aggregate_summary for a bias version, hoisting sibling-level fields like
 * `developer_recommendations` into the summary when the API placed them next to it.
 * Without this, dashboard payloads that ship `developer_recommendations` as a sibling of
 * `aggregate_summary` produce empty Issue Priority Queue entries (falling back to synthetic copy).
 */
const buildAggregateSummary = (node: any): Record<string, unknown> => {
  if (!node || typeof node !== 'object') return {};
  const base: Record<string, unknown> = { ...(node.aggregate_summary || {}) };
  for (const key of HOISTABLE_AGG_KEYS) {
    if (!isPopulatedObject(base[key]) && isPopulatedObject(node[key])) {
      base[key] = node[key];
    }
  }
  return base;
};

const resolveBiasKey = (biasSection: any, candidates: string[]) => {
  if (!biasSection || typeof biasSection !== 'object') return undefined;
  for (const key of candidates) {
    if (biasSection[key]) {
      return biasSection[key];
    }
  }
  const values = Object.values(biasSection);
  const hasAggregateData = (item: any) => {
    if (!item || typeof item !== 'object') return false;
    const check = (entry: any) =>
      entry && typeof entry === 'object' && (entry.aggregate_summary || entry.data || entry.entries);
    if (Array.isArray(item)) {
      return item.some(check);
    }
    return check(item);
  };
  return values.find(hasAggregateData);
};

const normalizeVersionEntries = <T extends { data?: any; entries?: any }>(version: T) => {
  if (!version) return version;
  const entries =
    Array.isArray(version.data) && version.data.length > 0
      ? version.data
      : Array.isArray((version as any).entries)
      ? (version as any).entries
      : [];
  return {
    ...version,
    data: entries
  };
};

const normalizeLlmEntry = (entry: any) => {
  const biasAnalysis = entry?.bias_analysis || {};
  return {
    Entry_Label: entry?.Entry_Label || entry?.entry_label || '',
    prompt: entry?.prompt || '',
    prompt_outputs: entry?.prompt_outputs || [],
    bias_analysis: biasAnalysis,
    Bias_Risk_Score: biasAnalysis?.Bias_Risk_Score ?? biasAnalysis?.bias_risk_score ?? 0,
    Risk_Level: biasAnalysis?.Risk_Level ?? biasAnalysis?.risk_level ?? 'Unknown',
    Verdict: biasAnalysis?.Verdict ?? biasAnalysis?.verdict ?? 'UNKNOWN',
    Status: biasAnalysis?.Status ?? biasAnalysis?.status ?? '',
    WEAT_Avg: biasAnalysis?.WEAT_Avg ?? biasAnalysis?.weat_avg ?? 0,
    StereoSet: biasAnalysis?.StereoSet ?? biasAnalysis?.stereo_set ?? 0,
    CrowsPairs: biasAnalysis?.CrowsPairs ?? biasAnalysis?.crows_pairs ?? 0,
    Toxicity: biasAnalysis?.Toxicity ?? biasAnalysis?.toxicity ?? 0,
    Sensitive_Attributes_Found:
      biasAnalysis?.Sensitive_Attributes_Found ?? biasAnalysis?.sensitive_attributes_found ?? {},
    NonWhitelisted_Terms: biasAnalysis?.NonWhitelisted_Terms ?? biasAnalysis?.non_whitelisted_terms ?? [],
    Whitelisted_Terms: biasAnalysis?.Whitelisted_Terms ?? biasAnalysis?.whitelisted_terms ?? [],
    Comment: biasAnalysis?.Comment ?? biasAnalysis?.comment ?? ''
  };
};

const detectBiasPayloadType = (node: any): 'ml' | 'llm' | null => {
  if (!node || typeof node !== 'object') return null;

  const rawType = String(node.analysis_type || '').toLowerCase();
  if (rawType.includes('llm')) return 'llm';
  if (rawType.includes('ml')) return 'ml';

  const rawTitle = String(node.title || '').toLowerCase();
  if (rawTitle === 'llm' || rawTitle.includes('llm')) return 'llm';
  if (rawTitle === 'ml' || rawTitle.includes('ml')) return 'ml';

  const entries = Array.isArray(node.entries) ? node.entries : Array.isArray(node.data) ? node.data : [];
  if (entries.length === 0) return null;
  const first = entries[0];
  if (first?.bias_analysis || first?.prompt_outputs || first?.prompt) return 'llm';
  if (first?.attribute || first?.Attribute || first?.disparity_ratio || first?.DisparityRatio) return 'ml';
  return null;
};

const normalizeBiasPayloadToVersion = (
  node: any
): { type: 'ml' | 'llm'; version: MLBiasDetectionItem | LLMBiasDetectionItem } | null => {
  const payloadType = detectBiasPayloadType(node);
  if (!payloadType) return null;
  if (!isWorkbenchNonRerunRun(node)) return null;

  const entries = Array.isArray(node.entries) ? node.entries : Array.isArray(node.data) ? node.data : [];

  if (payloadType === 'llm') {
    return {
      type: 'llm',
      version: normalizeVersionEntries({
        result_id: node.result_id || 'llm-bias',
        title: node.title || 'LLM Bias Detection',
        timestamp: node.timestamp || '',
        analysis_date: node.analysis_date || node.timestamp || '',
        use_whitelist: node.use_whitelist || '',
        domain: node.domain || '',
        userId: node.userId || '',
        aggregate_summary: buildAggregateSummary(node),
        data: entries.map((entry: any) => normalizeLlmEntry(entry))
      } as LLMBiasDetectionItem)
    };
  }

  return {
    type: 'ml',
    version: normalizeVersionEntries({
      result_id: node.result_id || 'ml-bias',
      title: node.title || 'ML Bias Detection',
      timestamp: node.timestamp || '',
      analysis_date: node.analysis_date || node.timestamp || '',
      use_whitelist: node.use_whitelist || '',
      domain: node.domain || '',
      userId: node.userId || '',
      aggregate_summary: buildAggregateSummary(node),
      data: entries
    } as MLBiasDetectionItem)
  };
};

export function BiasVersionHistoryStrip({
  llmCount,
  mlCount,
  onOpenLlm,
  onOpenMl
}: {
  llmCount: number;
  mlCount: number;
  onOpenLlm: () => void;
  onOpenMl: () => void;
}) {
  if (llmCount === 0 && mlCount === 0) {
    return null;
  }

  return (
    <div className="d-flex flex-wrap align-items-center gap-2 gap-sm-3 lh-sm small text-body-secondary">
      {llmCount > 0 && (
        <span className="d-inline-flex align-items-center gap-2">
          <span>
            LLM <span className="text-body fw-medium">{llmCount}</span> {llmCount === 1 ? 'version' : 'versions'}
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm text-decoration-none p-0 border-0"
            style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
            onClick={onOpenLlm}
          >
            History
          </button>
        </span>
      )}
      {llmCount > 0 && mlCount > 0 && (
        <span className="text-body-tertiary d-none d-sm-inline" aria-hidden="true">
          {'·'}
        </span>
      )}
      {mlCount > 0 && (
        <span className="d-inline-flex align-items-center gap-2">
          <span>
            ML <span className="text-body fw-medium">{mlCount}</span> {mlCount === 1 ? 'version' : 'versions'}
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm text-decoration-none p-0 border-0"
            style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
            onClick={onOpenMl}
          >
            History
          </button>
        </span>
      )}
    </div>
  );
}

interface BiasDetectionProps {
  onViewAllClick?: (type: 'ml' | 'llm') => void;
  onVersionsReady?: (mlCount: number, llmCount: number) => void;
  /** When false, the sticky LLM/ML version strip is hidden (parent can render it in the tab bar). Default true. */
  showHeaderVersionStrip?: boolean;
}

export interface BiasDetectionRef {
  handleHistoryOpen: (type: 'ml' | 'llm') => void;
}

const BiasDetection = forwardRef<BiasDetectionRef, BiasDetectionProps>(
  ({ onViewAllClick, onVersionsReady, showHeaderVersionStrip = true }, ref) => {
    const dispatch = useDispatch();
    const projectResults = useSelector(selectProjectResults);

    const [mlBiasVersions, setMlBiasVersions] = useState<MLBiasDetectionItem[]>([]);
    const [llmBiasVersions, setLlmBiasVersions] = useState<LLMBiasDetectionItem[]>([]);
    const [selectedMlVersion, setSelectedMlVersion] = useState<MLBiasDetectionItem | null>(null);
    const [selectedLlmVersion, setSelectedLlmVersion] = useState<LLMBiasDetectionItem | null>(null);
    const [isHeaderSticky, setIsHeaderSticky] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);
    const stickyTopPxRef = useRef(readPhoenixNavbarTopPx());
    const [historyOpen, setHistoryOpen] = useState(false);
    const [activeHistoryType, setActiveHistoryType] = useState<'ml' | 'llm'>('llm');
    const globalAggregateSummary = useMemo(() => {
      if (!projectResults || typeof projectResults !== 'object') {
        return undefined;
      }

      // When bias/bias_llm is an array of runs, use the run with the newest timestamp.
      // We pass each candidate through buildAggregateSummary so sibling-level fields like
      // `developer_recommendations` get hoisted into aggregate_summary before downstream consumers read it.
      const fromBiasArray = (arr: unknown) => {
        const item = pickLatestCollectionRun(arr as { aggregate_summary?: unknown }[] | null | undefined);
        if (!item || typeof item !== 'object') return undefined;
        const agg = buildAggregateSummary(item as Record<string, unknown>);
        return Object.keys(agg).length > 0 ? agg : undefined;
      };

      const fromBiasNode = (node: unknown) => {
        if (!node || typeof node !== 'object' || Array.isArray(node)) return undefined;
        const agg = buildAggregateSummary(node as Record<string, unknown>);
        return Object.keys(agg).length > 0 ? agg : undefined;
      };

      const resultsBias = projectResults?.results?.bias;
      const biasFirstAggregateSummary =
        Array.isArray(resultsBias) && resultsBias.length > 0 && resultsBias[0] && typeof resultsBias[0] === 'object'
          ? buildAggregateSummary(resultsBias[0] as Record<string, unknown>)
          : undefined;

      const potentialSummaries = [
        projectResults?.results?.aggregate_summary,
        biasFirstAggregateSummary,
        fromBiasNode(projectResults?.results?.bias),
        fromBiasArray(resultsBias),
        fromBiasNode(projectResults?.results?.bias_llm),
        fromBiasArray(projectResults?.results?.bias_llm),
        projectResults?.feature_results?.aggregate_summary,
        fromBiasNode(projectResults?.feature_results?.bias),
        fromBiasArray(projectResults?.feature_results?.bias),
        fromBiasNode(projectResults?.feature_results?.bias_llm),
        fromBiasArray(projectResults?.feature_results?.bias_llm),
        projectResults?.collection_results?.aggregate_summary,
        fromBiasNode(projectResults?.collection_results?.bias),
        fromBiasArray(projectResults?.collection_results?.bias),
        fromBiasNode(projectResults?.collection_results?.bias_llm),
        fromBiasArray(projectResults?.collection_results?.bias_llm)
      ];

      const picked = pickBestAggregateSummary(potentialSummaries);
      if (!picked) return undefined;
      return coalesceAggregateScalarsFromCandidates(picked, potentialSummaries);
    }, [projectResults]);

    /** Prefer the selected LLM version’s aggregate; merge sparse version blobs with project-level summary. */
    const activeAggregateSummary = useMemo((): Record<string, unknown> | null => {
      const fromVersion = selectedLlmVersion?.aggregate_summary as Record<string, unknown> | undefined;
      const g = globalAggregateSummary as Record<string, unknown> | undefined;
      return mergeSparseAggregateSummary(fromVersion, g);
    }, [selectedLlmVersion, globalAggregateSummary]);

    const mlChartRows = useMemo((): BiasMLDataItem[] => {
      const rows = selectedMlVersion?.data;
      if (!Array.isArray(rows) || rows.length === 0) return [];
      return rows.map((row: Record<string, unknown>) => ({
        Attribute: String(row.Attribute ?? row.attribute ?? '—'),
        DisparityRatio: Number(row.DisparityRatio ?? row.disparity_ratio ?? 0),
        CalculatedImpact:
          row.CalculatedImpact != null || row.calculated_impact != null
            ? Number(row.CalculatedImpact ?? row.calculated_impact)
            : undefined,
        BiasDetected: (row.BiasDetected ?? row.bias_detected) as boolean | undefined,
        Risk: row.Risk != null ? String(row.Risk) : row.risk != null ? String(row.risk) : undefined,
        Action: row.Action != null ? String(row.Action) : row.action != null ? String(row.action) : undefined,
        BiasDrift:
          row.BiasDrift != null ? String(row.BiasDrift) : row.bias_drift != null ? String(row.bias_drift) : undefined
      }));
    }, [selectedMlVersion]);

    useEffect(() => {
      const sections: any[] = [];

      const rootResults = projectResults?.results;
      if (rootResults && typeof rootResults === 'object') {
        // Handle bias as array - push each version so View All flyout shows all
        if (rootResults.bias) {
          if (Array.isArray(rootResults.bias) && rootResults.bias.length > 0) {
            rootResults.bias.forEach((item: any) => {
              if (isWorkbenchNonRerunRun(item)) sections.push(item);
            });
          } else if (typeof rootResults.bias === 'object' && isWorkbenchNonRerunRun(rootResults.bias)) {
            sections.push(rootResults.bias);
          }
        }
        // Handle bias_llm as array - push each version
        if (rootResults.bias_llm) {
          if (Array.isArray(rootResults.bias_llm) && rootResults.bias_llm.length > 0) {
            rootResults.bias_llm.forEach((item: any) => {
              if (isWorkbenchNonRerunRun(item)) sections.push(item);
            });
          } else if (typeof rootResults.bias_llm === 'object' && isWorkbenchNonRerunRun(rootResults.bias_llm)) {
            sections.push(rootResults.bias_llm);
          }
        }
        sections.push(rootResults);
      }

      const featureResults = projectResults?.feature_results;
      if (featureResults && typeof featureResults === 'object') {
        // Handle bias as array - push each version so View All flyout shows all
        if (featureResults.bias) {
          if (Array.isArray(featureResults.bias) && featureResults.bias.length > 0) {
            featureResults.bias.forEach((item: any) => {
              if (isWorkbenchNonRerunRun(item)) sections.push(item);
            });
          } else if (typeof featureResults.bias === 'object' && isWorkbenchNonRerunRun(featureResults.bias)) {
            sections.push(featureResults.bias);
          }
        }
        // Handle bias_llm as array - push each version
        if (featureResults.bias_llm) {
          if (Array.isArray(featureResults.bias_llm) && featureResults.bias_llm.length > 0) {
            featureResults.bias_llm.forEach((item: any) => {
              if (isWorkbenchNonRerunRun(item)) sections.push(item);
            });
          } else if (typeof featureResults.bias_llm === 'object' && isWorkbenchNonRerunRun(featureResults.bias_llm)) {
            sections.push(featureResults.bias_llm);
          }
        }
        sections.push(featureResults);
      }

      const collectionResults = projectResults?.collection_results;
      if (collectionResults && typeof collectionResults === 'object') {
        let pushedBiasItem = false;
        // Handle bias as array - push each version so View All flyout shows all
        if (collectionResults.bias) {
          if (Array.isArray(collectionResults.bias) && collectionResults.bias.length > 0) {
            collectionResults.bias.forEach((item: any) => {
              if (isWorkbenchNonRerunRun(item)) {
                sections.push(item);
                pushedBiasItem = true;
              }
            });
          } else if (typeof collectionResults.bias === 'object' && isWorkbenchNonRerunRun(collectionResults.bias)) {
            sections.push(collectionResults.bias);
            pushedBiasItem = true;
          }
        }
        // Handle bias_llm as array - push each version
        if (collectionResults.bias_llm) {
          if (Array.isArray(collectionResults.bias_llm) && collectionResults.bias_llm.length > 0) {
            collectionResults.bias_llm.forEach((item: any) => {
              if (isWorkbenchNonRerunRun(item)) {
                sections.push(item);
                pushedBiasItem = true;
              }
            });
          } else if (
            typeof collectionResults.bias_llm === 'object' &&
            isWorkbenchNonRerunRun(collectionResults.bias_llm)
          ) {
            sections.push(collectionResults.bias_llm);
            pushedBiasItem = true;
          }
        }
        // Only push the whole collection_results if we didn't push a specific bias/bias_llm item
        // (avoids duplicate version with empty aggregate_summary when data lives only in bias[0])
        if (!pushedBiasItem) {
          sections.push(collectionResults);
        }
      }

      const llmCollected: LLMBiasDetectionItem[] = [];
      const mlCollected: MLBiasDetectionItem[] = [];

      sections.forEach(section => {
        if (section && typeof section === 'object' && !Array.isArray(section) && (section as any).is_rerun === true) {
          return;
        }
        // Handle arrays of version objects / direct bias payloads.
        if (Array.isArray(section) && section.length > 0) {
          section.forEach(item => {
            const normalized = normalizeBiasPayloadToVersion(item);
            if (normalized?.type === 'llm') {
              llmCollected.push(normalized.version as LLMBiasDetectionItem);
              return;
            }
            if (normalized?.type === 'ml') {
              mlCollected.push(normalized.version as MLBiasDetectionItem);
              return;
            }

            if (!item || typeof item !== 'object' || !isWorkbenchNonRerunRun(item)) return;

            const llmBiasData =
              item.LLM_BIAS ||
              item.LLMBiasDetection ||
              item.llmBiasDetection ||
              item.llm_bias_detection ||
              item['LLM_Bias_Detection'] ||
              item['LLM Bias Detection'] ||
              item.bias_llm ||
              item.biasLLM ||
              item.BiasLLM;

            if (llmBiasData && Array.isArray(llmBiasData)) {
              llmCollected.push(
                normalizeVersionEntries({
                  result_id: item.result_id || 'llm-bias',
                  title: item.title || 'LLM Bias Detection',
                  timestamp: item.timestamp || '',
                  analysis_date: item.timestamp || '',
                  use_whitelist: item.use_whitelist || '',
                  domain: item.domain || '',
                  userId: item.userId || '',
                  aggregate_summary: buildAggregateSummary(item),
                  data: llmBiasData.map((entry: any) => normalizeLlmEntry(entry))
                } as LLMBiasDetectionItem)
              );
            }

            const mlBiasDetection = item.ML_Bias_Detection;
            if (mlBiasDetection && Array.isArray(mlBiasDetection) && mlBiasDetection.length > 0) {
              mlCollected.push(
                normalizeVersionEntries({
                  result_id: item.result_id || 'ml-bias',
                  title: item.title || 'ML Bias Detection',
                  timestamp: item.timestamp || '',
                  data: mlBiasDetection
                } as MLBiasDetectionItem)
              );
            }
          });
          return; // Skip the rest of the per-section logic for arrays
        }

        // Handle non-array sections (original logic)
        const normalizedSection = normalizeBiasPayloadToVersion(section);
        if (normalizedSection?.type === 'llm') {
          llmCollected.push(normalizedSection.version as LLMBiasDetectionItem);
          return;
        }
        if (normalizedSection?.type === 'ml') {
          mlCollected.push(normalizedSection.version as MLBiasDetectionItem);
          return;
        }

        const llmRaw = resolveBiasKey(section, [
          'LLM_BIAS',
          'LLMBiasDetection',
          'llmBiasDetection',
          'llm_bias_detection',
          'LLM_Bias_Detection',
          'LLM Bias Detection',
          'bias_llm',
          'biasLLM',
          'BiasLLM'
        ]);

        if (Array.isArray(llmRaw)) {
          // Check if it's the new LLM_BIAS structure
          if (llmRaw.length > 0 && llmRaw[0]?.bias_analysis) {
            // Transform LLM_BIAS array
            const transformedData = llmRaw.map((entry: any) => normalizeLlmEntry(entry));

            if (isWorkbenchNonRerunRun(section)) {
              llmCollected.push(
                normalizeVersionEntries({
                  result_id: (section as any).result_id || 'llm-root',
                  title: (section as any).title || 'LLM Bias Detection',
                  timestamp: (section as any).timestamp || '',
                  analysis_date: (section as any).timestamp || '',
                  use_whitelist: (section as any).use_whitelist || '',
                  domain: (section as any).domain || '',
                  userId: (section as any).userId || '',
                  aggregate_summary: buildAggregateSummary(section),
                  data: transformedData
                } as LLMBiasDetectionItem)
              );
            }
          } else {
            llmRaw.forEach(item => {
              if (isWorkbenchNonRerunRun(item)) llmCollected.push(normalizeVersionEntries(item));
            });
          }
        } else if (llmRaw) {
          if (isWorkbenchNonRerunRun(llmRaw)) llmCollected.push(normalizeVersionEntries(llmRaw));
        } else if (
          section &&
          typeof section === 'object' &&
          ((section as any).entries || (section as any).data || (section as any).aggregate_summary)
        ) {
          if (isWorkbenchNonRerunRun(section)) {
            llmCollected.push(
              normalizeVersionEntries({
                result_id: (section as any).result_id || 'llm-root',
                title: (section as any).title || 'LLM Bias Detection',
                timestamp: (section as any).timestamp || '',
                analysis_date: (section as any).timestamp || '',
                use_whitelist: (section as any).use_whitelist || '',
                domain: (section as any).domain || '',
                userId: (section as any).userId || '',
                aggregate_summary: buildAggregateSummary(section),
                data: (section as any).entries || (section as any).data || []
              } as LLMBiasDetectionItem)
            );
          }
        }

        const mlCandidates = [
          section?.ML_Bias_Detection,
          section?.mlBiasDetection,
          section?.ml_bias_detection,
          section?.MLBiasDetection
        ];

        mlCandidates.filter(Boolean).forEach(item => {
          if (Array.isArray(item)) {
            // Check if this is an array of data items (has Attribute, DisparityRatio, etc.)
            // or an array of version objects (has result_id, title, data, etc.)
            if (item.length > 0) {
              const firstItem = item[0];
              // If first item has Attribute or DisparityRatio, it's data items
              if (
                firstItem?.Attribute ||
                firstItem?.DisparityRatio ||
                firstItem?.attribute ||
                firstItem?.disparityRatio
              ) {
                // This is an array of data items - wrap it in a version object
                mlCollected.push(
                  normalizeVersionEntries({
                    result_id: (section as any)?.result_id || 'ml-bias',
                    title: (section as any)?.title || 'ML Bias Detection',
                    timestamp: (section as any)?.timestamp || '',
                    data: item
                  } as MLBiasDetectionItem)
                );
              } else {
                // This is an array of version objects
                item.forEach(entry => {
                  if (isWorkbenchNonRerunRun(entry)) mlCollected.push(normalizeVersionEntries(entry));
                });
              }
            }
          } else if (item && typeof item === 'object') {
            // Check if it's a data object (has Attribute) or a version object (has data/entries)
            if (item.Attribute || item.DisparityRatio || item.attribute || item.disparityRatio) {
              // It's a single data item - wrap it in a version object
              mlCollected.push(
                normalizeVersionEntries({
                  result_id: (section as any)?.result_id || 'ml-bias',
                  title: (section as any)?.title || 'ML Bias Detection',
                  timestamp: (section as any)?.timestamp || '',
                  data: [item]
                } as MLBiasDetectionItem)
              );
            } else {
              // It's a version object
              if (isWorkbenchNonRerunRun(item)) {
                mlCollected.push(
                  normalizeVersionEntries({
                    title: item.title || 'ML Analysis',
                    ...item
                  })
                );
              }
            }
          }
        });
      });

      // Deduplicate by result_id so the same run from bias vs bias_llm (or results vs feature_results) doesn't appear twice in View All
      const dedupeLlm = (items: LLMBiasDetectionItem[]): LLMBiasDetectionItem[] => {
        const seen = new Set<string>();
        return items.filter(item => {
          const id = item.result_id || `${item.title ?? ''}-${JSON.stringify(item.timestamp ?? '')}`;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      };
      const dedupeMl = (items: MLBiasDetectionItem[]): MLBiasDetectionItem[] => {
        const seen = new Set<string>();
        return items.filter(item => {
          const id = item.result_id || `${item.title ?? ''}-${JSON.stringify(item.timestamp ?? '')}`;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      };
      const llmDeduped = dedupeLlm(llmCollected);
      const mlDeduped = dedupeMl(mlCollected);

      setLlmBiasVersions(llmDeduped);
      setMlBiasVersions(mlDeduped);

      if (llmDeduped.length > 0) {
        const firstLlm = llmDeduped[0];
        setSelectedLlmVersion(firstLlm);
      } else {
        setSelectedLlmVersion(null);
      }

      if (mlDeduped.length > 0) {
        const firstMl = mlDeduped[0];
        setSelectedMlVersion(firstMl);
      } else {
        setSelectedMlVersion(null);
      }
    }, [projectResults]);

    useLayoutEffect(() => {
      const syncTop = () => {
        stickyTopPxRef.current = readPhoenixNavbarTopPx();
      };
      syncTop();
      window.addEventListener('resize', syncTop);
      return () => window.removeEventListener('resize', syncTop);
    }, []);

    useEffect(() => {
      const handleScroll = () => {
        if (headerRef.current) {
          const rect = headerRef.current.getBoundingClientRect();
          setIsHeaderSticky(rect.top <= stickyTopPxRef.current + 0.5);
        }
      };

      handleScroll();
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleVersionSelect = (type: 'ml' | 'llm', version: MLBiasDetectionItem | LLMBiasDetectionItem) => {
      if (type === 'ml') {
        setSelectedMlVersion(version as MLBiasDetectionItem);
      } else {
        setSelectedLlmVersion(version as LLMBiasDetectionItem);
        // Dispatch selected LLM version to Redux for all dashboard cards
        dispatch(setSelectedVersion(version));
      }
    };

    // Convert bias detection items to version items for the history component
    const convertToVersionItems = (
      versions: (MLBiasDetectionItem | LLMBiasDetectionItem)[],
      type: 'ml' | 'llm'
    ): VersionItem[] => {
      const selectedVersion = type === 'ml' ? selectedMlVersion : selectedLlmVersion;

      return versions.map((version, index) => {
        const timestampString = formatBiasVersionTimestamp(version.timestamp);

        return {
          id: version.result_id,
          timestamp: timestampString,
          date: timestampString,
          modifier: version.userId || 'System',
          isCurrent: selectedVersion?.result_id === version.result_id,
          title: version.title || `Version ${index + 1}` // Use the title from data, fallback to "Version X"
        };
      });
    };

    const handleHistoryOpen = (type: 'ml' | 'llm') => {
      setActiveHistoryType(type);
      setHistoryOpen(true);
      // Call parent callback if provided
      if (onViewAllClick) {
        onViewAllClick(type);
      }
    };

    // Expose handleHistoryOpen via ref
    useImperativeHandle(ref, () => ({
      handleHistoryOpen
    }));

    // Notify parent when versions are ready
    useEffect(() => {
      if (onVersionsReady) {
        onVersionsReady(mlBiasVersions.length, llmBiasVersions.length);
      }
    }, [mlBiasVersions.length, llmBiasVersions.length, onVersionsReady]);

    const handleVersionHistorySelect = (versionItem: VersionItem) => {
      const versions = activeHistoryType === 'ml' ? mlBiasVersions : llmBiasVersions;
      const version = versions.find(v => v.result_id === versionItem.id);
      if (version) {
        handleVersionSelect(activeHistoryType, version);
      }
    };

    const handleRename = (versionItem: VersionItem, newName: string) => {
      // Implement rename functionality
      console.log('Rename version:', versionItem.id, 'to:', newName);
    };

    const handleRestore = (versionItem: VersionItem) => {
      // Implement restore functionality
      console.log('Restore version:', versionItem.id);
    };

    const handleCopy = (versionItem: VersionItem) => {
      // Implement copy functionality
      console.log('Copy version:', versionItem.id);
    };

    const handleCompare = (version1: VersionItem, version2: VersionItem) => {
      // Implement compare functionality
      console.log('Compare versions:', version1.id, 'vs', version2.id);
    };

    return (
      <div>
        {/* Sticky header with version strip — optional when parent renders versions in the tab bar */}
        {showHeaderVersionStrip ? (
          <div
            ref={headerRef}
            style={{
              position: 'sticky',
              top: 'var(--phoenix-navbar-top-height, 72px)',
              zIndex: 1100,
              backgroundColor: isHeaderSticky ? 'var(--bs-body-bg)' : 'transparent',
              borderBottom: isHeaderSticky ? '1px solid var(--bs-border-color-translucent)' : 'none',
              backdropFilter: isHeaderSticky ? 'blur(8px)' : 'none',
              boxShadow: isHeaderSticky ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
              marginBottom: '4px',
              transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
              width: '100%',
              left: 0,
              right: 0
            }}
          >
            <Container fluid className="px-0 px-sm-1 px-lg-2">
              <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 py-0 min-h-0">
                <BiasVersionHistoryStrip
                  llmCount={llmBiasVersions.length}
                  mlCount={mlBiasVersions.length}
                  onOpenLlm={() => handleHistoryOpen('llm')}
                  onOpenMl={() => handleHistoryOpen('ml')}
                />
              </div>
            </Container>
          </div>
        ) : (
          <div ref={headerRef} className="d-none" aria-hidden />
        )}

        <Container fluid className="px-0 px-sm-1 px-lg-2">
          <Row className="g-1 mb-0">
            <Col xs={12}>
              <Card className="border-0 shadow-sm rounded-4 overflow-hidden bg-body-secondary bg-opacity-10">
                <Card.Body className="p-2 p-md-2">
                  <BiasModuleKpiGrid aggregate={activeAggregateSummary} llmEntriesForRisk={selectedLlmVersion?.data} />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-1 mb-0">
            <Col xs={12}>
              <Card className="border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                <Card.Body className="p-2 p-md-2">
                  <BiasDeveloperRemediationSection
                    aggregateSummary={activeAggregateSummary}
                    entries={selectedLlmVersion?.data as Record<string, unknown>[] | undefined}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Accordion className="bias-detection-accordion border-0 rounded-4 shadow-sm overflow-hidden">
            <Accordion.Item eventKey="ml" className="border-0 bg-white">
              <Accordion.Header className="bias-detection-accordion__header px-2 py-1 fw-semibold">
                ML charts
              </Accordion.Header>
              <Accordion.Body className="p-2">
                {mlChartRows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" className="py-1">
                    No ML bias attribute rows for the selected version. Run ML bias detection or pick another version
                    from View All.
                  </Typography>
                ) : (
                  <>
                    <BiasMLChart
                      embedded
                      data={mlChartRows}
                      title="Disparity & impact by attribute"
                      description="Bars colored by risk level; hover a bar for full metrics."
                    />
                    <div className="table-responsive mt-1">
                      <table className="table table-sm table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Attribute</th>
                            <th>Risk</th>
                            <th>Disparity</th>
                            <th>Bias detected</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mlChartRows.map(row => (
                            <tr key={row.Attribute}>
                              <td className="fw-semibold">{row.Attribute}</td>
                              <td>{row.Risk ?? '—'}</td>
                              <td>{row.DisparityRatio != null ? row.DisparityRatio.toFixed(3) : '—'}</td>
                              <td>{row.BiasDetected === true ? 'Yes' : row.BiasDetected === false ? 'No' : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Container>

        {/* Version History Drawer */}
        <VersionHistory
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          versions={convertToVersionItems(
            activeHistoryType === 'ml' ? mlBiasVersions : llmBiasVersions,
            activeHistoryType
          )}
          onVersionSelect={handleVersionHistorySelect}
          onRename={handleRename}
          onRestore={handleRestore}
          onCopy={handleCopy}
          onCompare={handleCompare}
          title={`Version History - ${activeHistoryType.toUpperCase()}`}
        />
      </div>
    );
  }
);

BiasDetection.displayName = 'BiasDetection';

export default BiasDetection;
